import { Button } from "components/ui/Button/Button";
import { Loading } from "components/ui/Loading/Loading";
import { Textarea } from "components/ui/Textarea/Textarea";
import { useEmailCompose } from "hooks/use-email-compose";
import type { ReplyEmailItem } from "hooks/use-email-replies-api";
import { Maximize2, Sparkles } from "lucide-react";
import { type FC, useEffect, useState } from "react";
import { toast } from "react-toastify";
import { toastError } from "utils/toast-utils";

import { buildReplySeed } from "../lib/build-reply-seed";

const PROMPT_PLACEHOLDER =
  "AI に伝えたい内容を短く入力 (例: 単価70万円で再提案、来週水曜の打合せ候補を提示、丁寧にお断り)";

export type InlineReplyComposerProps = {
  /** 選択中の reply (返信先) */
  reply: ReplyEmailItem;
  /** Gmail 連携が有効か。false なら送信不可 */
  canCompose: boolean;
  /** フル機能の返信モーダルを開く (CC/BCC・送信元選択等) */
  onOpenFullEditor: () => void;
  /** 送信成功時のコールバック (一覧 refetch 等) */
  onSent: () => void;
};

/**
 * 返信管理画面の右ペイン下部に埋め込む簡易返信ペイン.
 *
 * AI 生成の操作は Gmail 風の「自由入力プロンプト」 1 つ。 ユーザは短い指示を書き
 * 「生成」 ボタンで AI が本文を生成する. プロンプトは promptType="custom" として
 * バックエンドへ送られ、 同スレッド全体を文脈として読み込んだ上で本文が生成される.
 * メール冒頭の挨拶ブロック (送り先会社名 / 担当者名 + 送り主の枕詞) はバックエンドの
 * `_build_prompt` で必ず付与される.
 *
 * @param {InlineReplyComposerProps} props 選択 reply とハンドラ
 * @return {JSX.Element} インライン返信ペイン
 */
export const InlineReplyComposer: FC<InlineReplyComposerProps> = ({
  reply,
  canCompose,
  onOpenFullEditor,
  onSent,
}) => {
  const {
    draft,
    isGenerating,
    isSending,
    error,
    startReplyFromSeed,
    setField,
    generateBody,
    sendEmail,
    cancelGeneration,
  } = useEmailCompose();

  const [promptText, setPromptText] = useState("");

  // **Identity ベース**で再シード. reply オブジェクトは refetch のたびに新参照になり
  // 中身が同じでも再生成されるため、 reply.id を deps にして
  // ユーザがすでに入力した本文・プロンプトを保護する。
  // ref(参照)を依存に入れると送信→refetch のたびに draft / promptText が wipe される.
  // biome-ignore lint/correctness/useExhaustiveDependencies: identity-only re-seed by design
  useEffect(() => {
    startReplyFromSeed(buildReplySeed(reply));
    setPromptText("");
  }, [reply.id, startReplyFromSeed]);

  /**
   * 送信を実行する
   * @return {Promise<void>}
   */
  const handleSend = async (): Promise<void> => {
    const success = await sendEmail();
    if (success) {
      toast.success("返信メールを送信しました");
      onSent();
    } else {
      toastError("メールの送信に失敗しました");
    }
  };

  /**
   * AI 生成を実行する (custom プロンプト + freeText)
   * @return {void}
   */
  const handleGenerate = (): void => {
    generateBody("custom", promptText.trim() || undefined);
  };

  if (!draft) {
    return (
      <div className="flex justify-center py-3">
        <Loading size="xs" />
      </div>
    );
  }

  const canGenerate =
    !isGenerating && !isSending && promptText.trim().length > 0;
  const canSend =
    canCompose && !isGenerating && !isSending && draft.body.trim().length > 0;

  return (
    <div className="space-y-2">
      <div className="flex items-center justify-between">
        <div className="text-xs text-base-content/60">
          返信先:{" "}
          <span className="font-medium text-base-content">
            {reply.fromName ? `${reply.fromName} ` : ""}
            <span className="text-base-content/60">
              &lt;{reply.fromEmail}&gt;
            </span>
          </span>
        </div>
        <Button
          type="button"
          variant="ghost"
          size="xs"
          onClick={onOpenFullEditor}
        >
          <span className="inline-flex items-center gap-1">
            <Maximize2 size={12} aria-hidden />
            フル画面で編集
          </span>
        </Button>
      </div>

      <div className="space-y-1">
        <div className="flex items-center justify-between">
          <span className="inline-flex items-center gap-1 text-xs text-violet-700">
            <Sparkles size={12} aria-hidden />
            AI 生成プロンプト
          </span>
          <span className="text-[10px] text-base-content/50">
            スレッド全体を読んだ上で生成します
          </span>
        </div>
        <div className="flex items-start gap-2">
          <div className="flex-1 min-w-0">
            <Textarea
              size="sm"
              rows={2}
              value={promptText}
              onChange={e => setPromptText(e.target.value)}
              placeholder={PROMPT_PLACEHOLDER}
              disabled={isGenerating || isSending}
              onKeyDown={e => {
                if ((e.metaKey || e.ctrlKey) && e.key === "Enter") {
                  e.preventDefault();
                  if (canGenerate) handleGenerate();
                }
              }}
            />
          </div>
          <Button
            type="button"
            variant="accent"
            size="sm"
            onClick={handleGenerate}
            disabled={!canGenerate}
            title={
              promptText.trim().length === 0
                ? "プロンプトを入力してください"
                : undefined
            }
          >
            {isGenerating ? (
              <span className="inline-flex items-center gap-1">
                <Loading size="xs" />
                生成中
              </span>
            ) : (
              "生成"
            )}
          </Button>
          {isGenerating && (
            <Button
              type="button"
              variant="ghost"
              size="xs"
              onClick={cancelGeneration}
            >
              停止
            </Button>
          )}
        </div>
      </div>

      <Textarea
        size="sm"
        rows={6}
        value={draft.body}
        onChange={e => setField("body", e.target.value)}
        placeholder={
          isGenerating
            ? "AI が本文を生成しています..."
            : "返信内容 (生成結果はここに反映 / 直接編集も可)"
        }
      />

      {error && (
        <div className="text-xs text-rose-600 break-words">{error}</div>
      )}

      <div className="flex justify-end">
        <Button
          type="button"
          size="sm"
          onClick={handleSend}
          disabled={!canSend}
          title={canCompose ? undefined : "返信送信には Gmail 連携が必要です"}
        >
          {isSending ? (
            <span className="inline-flex items-center gap-1">
              <Loading size="xs" />
              送信中
            </span>
          ) : (
            "送信"
          )}
        </Button>
      </div>
    </div>
  );
};
